'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';

import { FLUX_URL_DEV, FLUX_URL_PROD, SIGNUP_PATH } from '../../constants';

import { ExitConfirmationModalProvider } from './exit-confirmation';
import { LoggerProvider } from './logger/index';

import type { ReactNode } from 'react';

type Environment = 'dev' | 'draft' | 'singlegas' | 'nonprod' | 'prod';

const EnvContext = createContext<Environment>('dev');

type EnvironmentVariables = {
  env: Environment;
  LOGIN_URL: string;
  SIGNUP_URL: string;
  FORMSTACK_URL: string;
};

export const useEnvContext = (): EnvironmentVariables => {
  const env = useContext(EnvContext);

  if (env === undefined) {
    throw new Error('Component must be used within an EnvContextProvider');
  }

  let LOGIN_URL: string;
  switch (env) {
    case 'prod':
      LOGIN_URL = FLUX_URL_PROD;
      break;
    default:
      LOGIN_URL = FLUX_URL_DEV;
  }

  let FORMSTACK_URL: string;
  switch (env) {
    case 'nonprod':
    case 'prod':
      FORMSTACK_URL = `${process.env.NEXT_PUBLIC_FORMSTACK_URL}/forms/ev_plan_eligibility`;
      break;
    default:
      FORMSTACK_URL = `${process.env.NEXT_PUBLIC_FORMSTACK_URL}/forms/dev_env_ev_plan_eligibility`;
  }

  return {
    env,
    FORMSTACK_URL,
    LOGIN_URL,
    SIGNUP_URL: `${LOGIN_URL}${SIGNUP_PATH}`,
  };
};

export default function Providers({
  children,
  env,
}: {
  children?: ReactNode;
  env: 'dev' | 'draft' | 'singlegas' | 'nonprod' | 'prod';
}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
          },
        },
      })
  );

  return (
    <EnvContext.Provider value={env}>
      <QueryClientProvider client={queryClient}>
        <ExitConfirmationModalProvider>
          <LoggerProvider env={env}>{children}</LoggerProvider>
        </ExitConfirmationModalProvider>
      </QueryClientProvider>
    </EnvContext.Provider>
  );
}
